import type { TooltipProps } from '@neo4j-ndl/react';
import { IconButton, Menu, Tooltip } from '@neo4j-ndl/react';
import {
  ArrowDownTrayIconOutline,
  BoxSelectIcon,
  FitToScreenIcon,
  LassoIcon,
  MagnifyingGlassMinusIconOutline,
  MagnifyingGlassPlusIconOutline,
  SelectIcon,
  SidebarLineRightIcon,
} from '@neo4j-ndl/react/icons';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Gesture } from './graph-viz-context';
import { useGraphVizContext } from './graph-viz-context';

interface BaseProperties {
  tipContent: string;
  testid?: string;
  ariaLabel?: string;
  active?: boolean;
  disabled?: boolean;
  placement?: TooltipProps['placement'];
  onClick?: () => void;
  onMouseDown?: () => void;
}

export type GraphVizGroupButton = PropsWithChildren<BaseProperties>;

export function GroupButton(props: GraphVizGroupButton) {
  const { testid, active, ariaLabel, disabled, tipContent, onClick, onMouseDown, placement, children } = props;
  return (
    <Tooltip type="simple" placement={placement}>
      <Tooltip.Trigger hasButtonWrapper>
        <IconButton
          ariaLabel={ariaLabel ?? tipContent}
          size="small"
          isClean
          isGrouped
          isActive={active}
          isDisabled={disabled}
          onClick={onClick}
          htmlAttributes={{
            onMouseDown,
            'data-testid': testid,
          }}
        >
          {children}
        </IconButton>
      </Tooltip.Trigger>
      <Tooltip.Content>{tipContent}</Tooltip.Content>
    </Tooltip>
  );
}

const targetIsTextInput = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA'].includes(target.tagName);
};

const isEditingText = (e: KeyboardEvent): boolean => {
  return targetIsTextInput(e.target);
};

const GESTURE_KEYS: Record<Gesture, string> = {
  box: 'B',
  lasso: 'L',
  single: 'S',
};

const useRegisterKeyboardGestureSwitcher = (gesture: Gesture) => {
  const { setGesture } = useGraphVizContext();
  const handleKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (!isEditingText(evt) && setGesture !== undefined) {
        const upperKey = evt.key.toUpperCase();
        if (upperKey === GESTURE_KEYS[gesture]) {
          setGesture(gesture);
        }
      }
    },
    [gesture, setGesture],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};

const NON_BREAKING_SPACE = '\u00A0';
const TOOLTIP_PLACEMENT_RIGHT: TooltipProps['placement'] = 'right';

export function SingleSelectButton() {
  const { gesture, setGesture, interactionMode } = useGraphVizContext();
  useRegisterKeyboardGestureSwitcher('single');

  return (
    <GroupButton
      testid="gesture-individual-select"
      active={gesture === 'single'}
      disabled={interactionMode !== 'select'}
      ariaLabel="Individual Select Button"
      tipContent={`Individual Select ${NON_BREAKING_SPACE} ${GESTURE_KEYS.single}`}
      onClick={() => {
        setGesture?.('single');
      }}
      placement={TOOLTIP_PLACEMENT_RIGHT}
    >
      <SelectIcon aria-label="Individual Select" />
    </GroupButton>
  );
}

export function BoxSelectButton() {
  const { gesture, setGesture, interactionMode } = useGraphVizContext();
  useRegisterKeyboardGestureSwitcher('box');

  return (
    <GroupButton
      testid="gesture-box-select"
      disabled={interactionMode !== 'select'}
      active={gesture === 'box'}
      ariaLabel="Box Select Button"
      tipContent={`Box Select ${NON_BREAKING_SPACE} ${GESTURE_KEYS.box}`}
      onClick={() => {
        setGesture?.('box');
      }}
      placement={TOOLTIP_PLACEMENT_RIGHT}
    >
      <BoxSelectIcon aria-label="Box Select" />
    </GroupButton>
  );
}

export function LassoSelectButton() {
  const { gesture, setGesture, interactionMode } = useGraphVizContext();
  useRegisterKeyboardGestureSwitcher('lasso');

  return (
    <GroupButton
      testid="gesture-lasso-select"
      disabled={interactionMode !== 'select'}
      active={gesture === 'lasso'}
      ariaLabel="Lasso Select Button"
      tipContent={`Lasso Select ${NON_BREAKING_SPACE} ${GESTURE_KEYS.lasso}`}
      onClick={() => {
        setGesture?.('lasso');
      }}
      placement={TOOLTIP_PLACEMENT_RIGHT}
    >
      <LassoIcon aria-label="Lasso Select" />
    </GroupButton>
  );
}

export function ZoomInButton() {
  const { nvlInstance } = useGraphVizContext();

  const handleZoomIn = useCallback(() => {
    nvlInstance?.setZoom(nvlInstance.getScale() * 1.3);
  }, [nvlInstance]);

  return (
    <GroupButton placement="left" onClick={handleZoomIn} tipContent="Zoom in">
      <MagnifyingGlassPlusIconOutline />
    </GroupButton>
  );
}

export function ZoomOutButton() {
  const { nvlInstance } = useGraphVizContext();

  const handleZoomOut = useCallback(() => {
    nvlInstance?.setZoom(nvlInstance.getScale() * 0.7);
  }, [nvlInstance]);

  return (
    <GroupButton placement="left" onClick={handleZoomOut} tipContent="Zoom out">
      <MagnifyingGlassMinusIconOutline />
    </GroupButton>
  );
}

export function ZoomToFitButton() {
  const { nvlInstance } = useGraphVizContext();

  const handleZoomToFit = useCallback(() => {
    nvlInstance?.fit(nvlInstance.getNodes().map((node) => node.id));
  }, [nvlInstance]);

  return (
    <GroupButton placement="left" onClick={handleZoomToFit} tipContent="Zoom to fit">
      <FitToScreenIcon />
    </GroupButton>
  );
}

export function ToggleSidePanelButton() {
  const { sidepanel } = useGraphVizContext();
  if (!sidepanel) {
    throw new Error('Using the ToggleSidePanelButton requires having a sidepanel');
  }
  const { isSidePanelOpen, setIsSidePanelOpen } = sidepanel;
  return (
    <Tooltip placement="bottom" type="simple">
      <Tooltip.Trigger hasButtonWrapper>
        <IconButton
          size="small"
          onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
          isFloating
          ariaLabel="Toggle node properties panel"
          className="bg-palette-neutral-bg-weak"
          htmlAttributes={{
            'aria-pressed': isSidePanelOpen,
          }}
        >
          <SidebarLineRightIcon className="text-palette-neutral-text-weak" />
        </IconButton>
      </Tooltip.Trigger>
      <Tooltip.Content className="n-body-small">{isSidePanelOpen ? 'Close panel' : 'Open panel'}</Tooltip.Content>
    </Tooltip>
  );
}

export function DownloadButton() {
  const { nvlInstance } = useGraphVizContext();

  const [downloadMenuOpen, setOpen] = useState(false);
  const closeFileMenu = () => setOpen(false);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <Tooltip type="simple" placement="bottom">
        <Tooltip.Trigger hasButtonWrapper>
          <IconButton
            ref={downloadButtonRef}
            size="small"
            isFloating
            onClick={() => setOpen((o) => !o)}
            ariaLabel="Download"
          >
            <ArrowDownTrayIconOutline />
          </IconButton>
        </Tooltip.Trigger>
        <Tooltip.Content className="n-body-small">Download</Tooltip.Content>
      </Tooltip>
      <Menu isOpen={downloadMenuOpen} onClose={closeFileMenu} anchorRef={downloadButtonRef} className="mt-1">
        <Menu.Items>
          <Menu.Item
            title="Download as PNG"
            onClick={() => {
              nvlInstance?.saveToFile({});
              closeFileMenu();
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
}
